import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from '../../../../firebase';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
// useChat removed 2026-05 — Chat-from-email retired.
import { KTSVG } from '../../../../_metronic/helpers';
import { sanitizeHtml, escapeHtml } from '../../../utils/sanitizeHtml';
// useEmailPanel removed 2026-05 — only used by retired Chat-from-email handler.
// StartChatFromEmailModal removed 2026-05.

// Define or import a consistent EmailAttachment interface
interface EmailAttachmentFromFirestore {
  filename: string;
  url: string; // This is the key: the pre-signed GCS URL
  // Add other fields if needed by the modal, e.g., size, contentType
  // For now, assuming only filename and url are directly used for display
}

interface EmailViewModalProps {
  email: {
    id: string;
    subject: string;
    text: string;
    html?: string;
    snippet?: string;
    from: {
      address: string;
      text: string;
      name?: string;
    }[];
    date: any;
    formattedDate?: string;
    customerId?: string | null;
    attachments?: EmailAttachmentFromFirestore[];
  } | null;
  show: boolean;
  onHide: () => void;
}

const EmailViewModal: React.FC<EmailViewModalProps> = ({ email, show, onHide }) => {
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<{ url: string; name: string }[]>([]);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // 2026-05: useChat/closeEmailPanel were used by the retired
  // Chat-from-email flow. closeEmailPanel still imported in case
  // other surfaces want it later; useChat no longer needed.

  const adjustIframeHeight = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      setTimeout(() => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          const height = iframeRef.current.contentWindow.document.body.scrollHeight;
          iframeRef.current.style.height = `${height + 30}px`;
        }
      }, 100);
    }
  };

  const getStyledHtml = (content: string) => `
  <html>
    <head>
      <style>
        body {
          background-color: var(--bs-body-bg, #fff);
          color: var(--bs-body-color, #212529);
          font-family: inherit;
          padding: 1rem;
          margin: 0;
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
        }
        a {
          color: var(--bs-primary, #007bff);
        }
        img {
          max-width: 100%;
          height: auto;
        }
      </style>
      <base target="_blank">
    </head>
    <body>
      ${content}
    </body>
  </html>
  `;

  useEffect(() => {
    if (show && email) {
      // The email object from props is now expected to be complete.
      // We just need to decide what to render.

      // 1. Set attachments from the complete email object
          if (email.attachments && email.attachments.length > 0) {
        const uiAttachments = email.attachments.map((att) => ({
              name: att.filename,
          url: att.url,
            }));
            setAttachments(uiAttachments);
          } else {
        setAttachments([]);
          }

      // 2. Set body content, with robust fallbacks
      const emailHtml = email.html?.trim();
      const emailText = email.text?.trim();
      const emailSnippet = email.snippet?.trim();
              
      // SECURITY: every part of an inbound email is attacker-controlled. HTML is
      // sanitised and text/snippet are escaped before they go into the srcDoc, and
      // the iframe below is sandboxed with NO allow-scripts — so even a sanitiser
      // bypass cannot run script against the app's origin / Firebase session.
      if (emailHtml) {
        setHtmlContent(getStyledHtml(sanitizeHtml(emailHtml)));
      } else if (emailText) {
        setHtmlContent(getStyledHtml(`<div style="white-space: pre-wrap;">${escapeHtml(emailText)}</div>`));
      } else if (emailSnippet) {
        setHtmlContent(
          getStyledHtml(
            `<div style="white-space: pre-wrap;"><em>Preview:</em><br/>${escapeHtml(emailSnippet)}</div>`
          )
        );
            } else {
              setHtmlContent(getStyledHtml('<div>No content available for this email.</div>'));
            }
    } else {
      // Reset state when modal is hidden or email is not present
      setHtmlContent(null);
      setAttachments([]);
    }
  }, [email, show]);

  const formatDate = (timestamp: any) => {
    let date: Date;

    // Handle different timestamp formats
    if (!timestamp) {
      return 'Invalid date';
    }

    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      // Already a Date object
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      // ISO string
      date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
      // Unix timestamp in ms
      date = new Date(timestamp);
    } else {
      date = new Date(); // Fallback
    }

    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Chat-from-email handlers removed 2026-05.

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      dialogClassName='email-overlay-modal modal-fullscreen-lg-down'
      centered
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title className='w-100'>
          {email?.subject || 'Email'}
          <p className='text-muted fw-normal fs-6 mb-0'>
            From: {email?.from?.[0]?.name || email?.from?.[0]?.address || 'Unknown Sender'}
            <br />
            Date: {email ? formatDate(email.date) : ''}
          </p>
          </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {htmlContent ? (
          <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            // No allow-scripts: inbound email must never execute. allow-same-origin
            // is only there so adjustIframeHeight can measure the body (safe without
            // scripts); the popups flags keep links opening in a new tab.
            sandbox='allow-popups allow-popups-to-escape-sandbox allow-same-origin'
            style={{ width: '100%', border: 'none', minHeight: '400px' }}
            onLoad={adjustIframeHeight}
            title='Email Content'
          />
        ) : (
          <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
          {attachments.length > 0 && (
          <div className='w-100'>
            <div className='mb-2'>
              <small>
                <b>
                  <i className='bi bi-paperclip fs-5 me-1'></i> Attachments ({attachments.length})
                </b>
              </small>
            </div>
            <div className='d-flex flex-wrap gap-2'>
              {attachments.map((att, index) => (
                <a
                  href={att.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  key={index}
                  className='btn btn-sm btn-light-primary'
                    >
                  <i className='bi bi-download me-1'></i>
                  {att.name}
                    </a>
                ))}
            </div>
          </div>
        )}
        <div className='d-flex justify-content-end align-items-center w-100 mt-3'>
          <Button variant='light' onClick={onHide}>
            Close
          </Button>
          {/* 'Start Chat' button removed 2026-05. Open Andrei via the
              header [andrei] button to discuss this email. */}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailViewModal;